import { useMemo } from 'react'
import { Paperclip } from 'lucide-react'
import { KanbanBoard, type KanbanItem, type KanbanStage, Chip } from '@/components/ui'
import { usePreferences } from '@/shell/stores/preferences'
import type { SampleRequestSummary, Stage } from '../api/types'
import {
  BOARD_STAGES,
  needByUrgencyForStage,
  STAGE_SHORT_LABELS,
  STAGE_VARIANT,
  transitionAllowed,
} from '../utils/stage-meta'

const MODULE_ID = 'sample-requests'

interface SampleKanbanItem extends KanbanItem {
  request: SampleRequestSummary
}

export interface SampleRequestsKanbanProps {
  items: SampleRequestSummary[]
  selectedId?: string
  onSelect: (request: SampleRequestSummary) => void
  onTransition: (request: SampleRequestSummary, target: Stage) => void
  /**
   * When true, cards can't be dragged between stages (every transition is
   * rejected). Clicking a card to open its detail still works. Used for the
   * read-only `uviva` role — see ../utils/permissions.ts.
   */
  readOnly?: boolean
}

function kanbanAccent(
  v: 'neutral' | 'success' | 'warning' | 'danger' | 'info',
): 'neutral' | 'success' | 'warning' | 'danger' {
  return v === 'info' ? 'neutral' : v
}

const STAGE_BY_ID: Record<Stage, KanbanStage> = BOARD_STAGES.reduce(
  (acc, s) => {
    acc[s] = { id: s, label: STAGE_SHORT_LABELS[s], accent: kanbanAccent(STAGE_VARIANT[s]) }
    return acc
  },
  {} as Record<Stage, KanbanStage>,
)

function composeStageOrder(userOrder: ReadonlyArray<string>): KanbanStage[] {
  const seen = new Set<string>()
  const out: KanbanStage[] = []
  for (const id of userOrder) {
    if (id in STAGE_BY_ID && !seen.has(id)) {
      out.push(STAGE_BY_ID[id as Stage])
      seen.add(id)
    }
  }
  for (const s of BOARD_STAGES) {
    if (!seen.has(s)) {
      out.push(STAGE_BY_ID[s])
      seen.add(s)
    }
  }
  return out
}

export function SampleRequestsKanban({
  items,
  selectedId,
  onSelect,
  onTransition,
  readOnly = false,
}: SampleRequestsKanbanProps) {
  const userOrder = usePreferences((s) => s.kanbanStageOrder[MODULE_ID]) ?? []
  const setKanbanStageOrder = usePreferences((s) => s.setKanbanStageOrder)

  const stages = useMemo(() => composeStageOrder(userOrder), [userOrder])

  const kanbanItems: SampleKanbanItem[] = useMemo(
    () => items.map((request) => ({ id: request.id, stageId: request.status, request })),
    [items],
  )

  return (
    <KanbanBoard<SampleKanbanItem>
      stages={stages}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={
        readOnly
          ? () => false
          : (from, to) => transitionAllowed(from as Stage, to as Stage)
      }
      onAdvance={(item, _from, to) => onTransition(item.request, to as Stage)}
      onSelect={(item) => onSelect(item.request)}
      onStageReorder={(next) => setKanbanStageOrder(MODULE_ID, next)}
      renderCard={(item) => <SampleKanbanCard request={item.request} />}
      scrollRestorationKey="sample-requests"
    />
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function SampleKanbanCard({ request }: { request: SampleRequestSummary }) {
  const customer = request.customer_name || request.prospect_name
  return (
    <div
      className={`flex flex-col gap-1.5 ${
        request.overdue ? '-ml-3 border-l-2 border-l-danger pl-2.5' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-role-data-primary truncate" title={customer ?? ''}>
          {customer ?? <em className="text-tertiary">no customer</em>}
        </div>
        <span className="text-role-metadata flex flex-shrink-0 items-center gap-1">
          {request.labels?.length ? (
            <Paperclip
              size={12}
              className="text-tertiary"
              aria-label={`${request.labels.length} shipping label${request.labels.length > 1 ? 's' : ''}`}
            />
          ) : null}
          {request.request_number}
        </span>
      </div>
      {(() => {
        const urgency = needByUrgencyForStage(request.need_by_date, request.status)
        return urgency ? (
          <Chip size="xs" variant={urgency.variant}>
            {urgency.label}
          </Chip>
        ) : null
      })()}
      {request.need_by_date ? (
        <div className="text-role-metadata">Need by {formatDate(request.need_by_date)}</div>
      ) : null}
      {request.tracking_number ? (
        <div className="text-role-metadata truncate" title={request.tracking_number}>
          Tracking {request.tracking_number}
        </div>
      ) : null}
      {request.overdue ? (
        <Chip size="xs" variant="danger">
          Overdue to ship
        </Chip>
      ) : null}
      {request.assigned_to ? (
        <div className="text-role-metadata truncate">Owner {request.assigned_to}</div>
      ) : (
        <div className="text-role-metadata italic text-tertiary">Unassigned</div>
      )}
    </div>
  )
}
